import { useEffect, useRef, useState } from "react";
import { IconButton, Switch } from "@mui/material";
import LoadingButton from "@mui/lab/LoadingButton";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import { useScriptMode } from "../../../contexts/ScriptModeContext";
import { useToast } from "../../../components/ui/Toast";
import {
  useGetGroupSettingsQuery,
  useUpdateGroupSettingsMutation,
} from "../../../api/managementApi";
import { cls, useIsDark } from "../../hatim/soft/SoftKit";
import { greenButtonSx } from "../../profile/actionStyles";
import { soft } from "./insights/softClasses";
import {
  DEFAULT_EDIT_WINDOW_DAYS,
  MAX_EDIT_WINDOW_DAYS,
  normalizeEditWindow,
} from "../../../lib/editWindow";
import ConnectionState from "../../../components/ui/ConnectionState";

export const NOTIFICATION_ROWS = [
  { key: "muzakereCreated", title: "gs_notif_muzakere_title", desc: "gs_notif_muzakere_desc" },
  { key: "assessmentOpen", title: "gs_notif_assessment_title", desc: "gs_notif_assessment_desc" },
  { key: "goalUpdated", title: "gs_notif_goal_title", desc: "gs_notif_goal_desc" },
  { key: "groupJoined", title: "gs_notif_joined_title", desc: "gs_notif_joined_desc" },
  { key: "managerActivity", title: "gs_notif_manager_title", desc: "gs_notif_manager_desc" },
];

const QUICK_DAYS = [0, 3, 7, 14, 30];

/** Sunucudan eksik gelen anahtarlar varsayılan olarak açık sayılır. */
function normalizeNotifications(raw) {
  const out = {};
  NOTIFICATION_ROWS.forEach(({ key }) => {
    out[key] = raw && typeof raw[key] === "boolean" ? raw[key] : true;
  });
  return out;
}

// Marka renginde anahtar; koyu temada açık turkuaz, kapalıyken okunur iz.
const switchSx = (dark) => ({
  "& .MuiSwitch-switchBase.Mui-checked": { color: dark ? "#8FC9CA" : "#006A71" },
  "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
    backgroundColor: dark ? "#8FC9CA" : "#48A6A7",
    opacity: dark ? 0.55 : 0.6,
  },
  ...(dark
    ? {
        "& .MuiSwitch-switchBase:not(.Mui-checked)": { color: "rgba(255,255,255,0.85)" },
        "& .MuiSwitch-track": { backgroundColor: "rgba(255,255,255,0.45)", opacity: 1 },
      }
    : {}),
});

const sectionTitle = `px-5 pt-5 pb-2 text-sm font-medium tracking-wide ${soft.muted}`;

function SettingsSkeleton() {
  return (
    <div className="grid grid-cols-1 xl:grid-cols-2" data-testid="group-settings-skeleton">
      {[0, 1].map((col) => (
        <div key={col} className="flex flex-col">
          <span className={`mx-5 mt-5 mb-2 h-4 w-32 rounded ${cls.skeleton}`} />
          {[0, 1, 2, 3].map((i) => (
            <div key={i} className={`${soft.row} justify-between`}>
              <div className="flex flex-col gap-2 flex-1">
                <span className={`h-4 w-2/5 rounded ${cls.skeleton}`} />
                <span className={`h-3.5 w-4/5 rounded ${cls.skeleton}`} />
              </div>
              <span className={`h-5 w-9 rounded-full ${cls.skeleton}`} />
            </div>
          ))}
        </div>
      ))}
    </div>
  );
}

/**
 * Grup ayarları (yalnız yöneticiler): bildirim anahtarları ve hedef görünürlüğü
 * anında kaydedilir, geçmiş gün düzenleme penceresi Kaydet ile. Ebeveyn bileşen grup değişince
 * `key` ile yeniden kurar; yerel durum gruptan gruba taşınmaz.
 */
export default function GroupSettingsPanel({ groupId }) {
  const { t } = useScriptMode();
  const toast = useToast();
  const dark = useIsDark();

  const { data, isLoading, isFetching, isError, error, refetch } = useGetGroupSettingsQuery(
    { groupId },
    { skip: !groupId }
  );
  const [updateSettings] = useUpdateGroupSettingsMutation();
  const settings = data?.data?.settings;

  // Bildirimler iyimser: yerel kopya hemen değişir, hata olursa geri alınır.
  const [notifications, setNotifications] = useState(() =>
    normalizeNotifications(settings?.notifications)
  );
  const notificationsRef = useRef(notifications);
  const pendingRef = useRef(0);

  // Üyeler birbirinin hedeflerini görsün: bildirimler gibi iyimser kaydedilir.
  const [membersSeeGoals, setMembersSeeGoals] = useState(() => Boolean(settings?.membersSeeGoals));
  const goalsPendingRef = useRef(0);

  const savedDays = normalizeEditWindow(settings?.editWindowDays ?? DEFAULT_EDIT_WINDOW_DAYS);
  const [draftDays, setDraftDays] = useState(savedDays);
  const [savingDays, setSavingDays] = useState(false);

  // Sunucudan taze ayar gelince yerel kopyayı eşitle (bekleyen kayıt yoksa).
  useEffect(() => {
    if (!settings || pendingRef.current > 0) return;
    const next = normalizeNotifications(settings.notifications);
    notificationsRef.current = next;
    setNotifications(next);
  }, [settings]);

  useEffect(() => {
    if (!settings || goalsPendingRef.current > 0) return;
    setMembersSeeGoals(Boolean(settings.membersSeeGoals));
  }, [settings]);

  useEffect(() => {
    setDraftDays(savedDays);
  }, [savedDays]);

  const applyNotifications = (next) => {
    notificationsRef.current = next;
    setNotifications(next);
  };

  const handleToggle = async (key, value) => {
    const next = { ...notificationsRef.current, [key]: value };
    applyNotifications(next);
    pendingRef.current += 1;
    const result = await updateSettings({ groupId, notifications: next });
    pendingRef.current -= 1;
    if (result.error) {
      applyNotifications({ ...notificationsRef.current, [key]: !value });
      toast.error(result.error.message || t("gs_save_error"));
    }
  };

  const handleMembersSeeGoals = async (value) => {
    setMembersSeeGoals(value);
    goalsPendingRef.current += 1;
    const result = await updateSettings({ groupId, membersSeeGoals: value });
    goalsPendingRef.current -= 1;
    if (result.error) {
      setMembersSeeGoals(!value);
      toast.error(result.error.message || t("gs_save_error"));
    }
  };

  const handleSaveDays = async () => {
    setSavingDays(true);
    const result = await updateSettings({ groupId, editWindowDays: draftDays });
    setSavingDays(false);
    if (result.error) {
      toast.error(result.error.message || t("gs_save_error"));
      return;
    }
    toast.success(t("gs_saved"));
  };

  if (isLoading) {
    return (
      <div className="h-full min-h-0 overflow-y-auto">
        <SettingsSkeleton />
      </div>
    );
  }

  if (isError && !settings) {
    return (
      <ConnectionState
        error={error}
        onRetry={refetch}
        retrying={isFetching}
        message={t("gs_load_error")}
        compact
      />
    );
  }

  const daysDirty = draftDays !== savedDays;
  const setDays = (n) => setDraftDays(normalizeEditWindow(n));
  const dayLabel = (n) => (n === 0 ? t("gs_edit_today_only") : t("gs_edit_days", { n }));

  return (
    <div className="h-full min-h-0 overflow-y-auto">
      <div className="grid grid-cols-1 xl:grid-cols-2 xl:divide-x xl:divide-terakkiBorder xl:dark:divide-white/15">
        <section aria-labelledby="gs-notifications">
          <h3 id="gs-notifications" className={sectionTitle}>
            {t("gs_notifications_title")}
          </h3>
          <ul className="tk-stagger">
            {NOTIFICATION_ROWS.map(({ key, title, desc }) => (
              <li key={key} className={`${soft.row} justify-between`}>
                <div className="min-w-0 flex-1">
                  <div id={`gs-${key}`} className={`text-base ${soft.ink}`}>
                    {t(title)}
                  </div>
                  <div className={`text-sm mt-0.5 ${soft.muted}`}>{t(desc)}</div>
                </div>
                <Switch
                  checked={Boolean(notifications[key])}
                  onChange={(e) => handleToggle(key, e.target.checked)}
                  inputProps={{ "aria-labelledby": `gs-${key}` }}
                  sx={switchSx(dark)}
                />
              </li>
            ))}
          </ul>
          <p className={`px-5 py-3 text-sm ${soft.faint}`}>{t("gs_notifications_hint")}</p>

          <h3 id="gs-visibility" className={sectionTitle}>
            {t("gs_visibility_title")}
          </h3>
          <ul className="tk-stagger">
            <li className={`${soft.row} justify-between`}>
              <div className="min-w-0 flex-1">
                <div id="gs-membersSeeGoals" className={`text-base ${soft.ink}`}>
                  {t("gs_members_see_goals_title")}
                </div>
                <div className={`text-sm mt-0.5 ${soft.muted}`}>{t("gs_members_see_goals_desc")}</div>
              </div>
              <Switch
                checked={membersSeeGoals}
                onChange={(e) => handleMembersSeeGoals(e.target.checked)}
                inputProps={{ "aria-labelledby": "gs-membersSeeGoals" }}
                sx={switchSx(dark)}
              />
            </li>
          </ul>
        </section>

        <section aria-labelledby="gs-edit-window">
          <h3 id="gs-edit-window" className={sectionTitle}>
            {t("gs_edit_title")}
          </h3>
          <div className="tk-stagger">
            <div className={`${soft.row} flex-wrap justify-between`}>
              <p className={`text-base flex-1 min-w-[180px] ${soft.ink}`}>{t("gs_edit_question")}</p>
              <div className="flex items-center gap-2">
                <IconButton
                  size="small"
                  aria-label={t("gs_edit_decrease")}
                  disabled={draftDays <= 0}
                  onClick={() => setDays(draftDays - 1)}
                  className="!bg-terakkiBrandCloud dark:!bg-white/10"
                >
                  <RemoveIcon fontSize="small" className={soft.accent} />
                </IconButton>
                <div className="w-14 text-center" aria-live="polite">
                  <div className={`text-xl font-SemiBold leading-none ${soft.accent}`} data-testid="gs-days-value">
                    {draftDays}
                  </div>
                  <div className={`text-sm mt-1 ${soft.muted}`}>{t("gs_edit_unit")}</div>
                </div>
                <IconButton
                  size="small"
                  aria-label={t("gs_edit_increase")}
                  disabled={draftDays >= MAX_EDIT_WINDOW_DAYS}
                  onClick={() => setDays(draftDays + 1)}
                  className="!bg-terakkiBrandCloud dark:!bg-white/10"
                >
                  <AddIcon fontSize="small" className={soft.accent} />
                </IconButton>
              </div>
            </div>
            <div className={`${soft.row} flex-wrap gap-2`}>
              {QUICK_DAYS.map((n) => {
                const active = draftDays === n;
                return (
                  <button
                    key={n}
                    type="button"
                    aria-pressed={active}
                    onClick={() => setDays(n)}
                    className={`text-sm px-3 py-1.5 rounded-full transition-colors ${
                      active
                        ? "bg-terakkiPrimaryDark text-white dark:bg-terakkiBrandSoft dark:text-[#123B3D]"
                        : `${cls.chip} hover:bg-terakkiBrandMist dark:hover:bg-white/15`
                    }`}
                  >
                    {dayLabel(n)}
                  </button>
                );
              })}
            </div>
            <div className="px-5 py-4 flex flex-col gap-3">
              <p className={`text-sm ${soft.muted}`}>{t("gs_edit_hint")}</p>
              <LoadingButton
                variant="contained"
                disableElevation
                loading={savingDays}
                disabled={!daysDirty}
                onClick={handleSaveDays}
                sx={{ ...greenButtonSx(dark ? "dark" : "light"), borderRadius: 999, alignSelf: { xs: "stretch", sm: "flex-start" }, px: 4 }}
              >
                {t("gs_save")}
              </LoadingButton>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
}
